import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

interface AuthContextValue {
  session: Session | null
  user: User | null
  loading: boolean
  role: string | null
  isAdmin: boolean
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  user: null,
  loading: true,
  role: null,
  isAdmin: false,
})

async function loadRole(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('role')
    .eq('user_id', userId)
    .maybeSingle()

  if (error) {
    console.error('Failed to load admin role:', error)
    return null
  }

  return data?.role ?? null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [role, setRole] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const applySession = async (nextSession: Session | null) => {
    setSession(nextSession)
    setUser(nextSession?.user ?? null)

    if (!nextSession?.user?.id) {
      setRole(null)
      setLoading(false)
      return
    }

    const nextRole = await loadRole(nextSession.user.id)
    setRole(nextRole)
    setLoading(false)
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      applySession(data.session)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setLoading(true)
      applySession(nextSession)
    })

    return () => subscription.unsubscribe()
  }, [])

  const isAdmin = role === 'admin'

  return (
    <AuthContext.Provider value={{ session, user, loading, role, isAdmin }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)